import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import { DataTable, Text, Card, Progress } from '@scale/scaleui-radix';
import { CandidateMessageWithId } from '@scale/shared/types/chat-completion';
import {
  RubricAnnotation,
  RubricDimensionRating,
  RubricField,
  NumberField,
} from '@scale/shared/types/params/textcollection';
import cn from 'classnames';
import _ from 'lodash';
import { useMemo } from 'react';
interface RubricTableProps {
  candidates: CandidateMessageWithId[];
  allResponses: Record<string, TextCollectionResponse>;
  rubricField: RubricField;
  responseScoreField?: NumberField;
}

const RubricSummaryTable = ({
  candidates,
  allResponses,
  rubricField,
  responseScoreField,
}: RubricTableProps) => {
  const getResponseScore = (response: TextCollectionResponse, fieldId: string) => {
    return response?.annotations[fieldId]?.response[0] as number | undefined;
  };

  const getRubricAnnotation = (response: TextCollectionResponse, fieldId: string) => {
    return response?.annotations[fieldId]?.response[0] as RubricAnnotation | undefined;
  };

  const scoredResponses = useMemo(() => {
    const scoreMap: Record<number, CandidateMessageWithId[]> = {};
    if (!responseScoreField) {
      return scoreMap;
    }

    const scores = candidates
      .map(candidate => {
        const response = allResponses[candidate.id];
        return getResponseScore(response, responseScoreField.field_id);
      })
      .filter((score): score is number => score !== undefined)
      .sort((a, b) => a - b);

    scores.forEach(score => {
      scoreMap[score] = candidates.filter(candidate => {
        const response = allResponses[candidate.id];
        const responseScore = getResponseScore(response, responseScoreField.field_id);
        return responseScore === score;
      });
    });

    return scoreMap;
  }, [candidates, allResponses, responseScoreField]);

  const weightedScoreMap = useMemo(() => {
    const weightedScoreMap: Record<string, number> = {};
    if (!rubricField.enableWeightedScore) {
      return weightedScoreMap;
    }

    candidates.forEach(candidate => {
      const response = allResponses[candidate.id];
      const rubricAnnotation = getRubricAnnotation(response, rubricField.field_id);
      const criterias = rubricAnnotation?.criterias || [];
      const totalWeight = criterias.reduce((acc, criteria) => {
        const weight = criteria.weight && criteria.weight > 0 ? criteria.weight : 0;
        return acc + weight;
      }, 0);

      // Should not happen because we validate that at least one weight is positive
      if (totalWeight <= 0) {
        return weightedScoreMap;
      }

      const weightedScore = criterias.reduce((acc, criteria) => {
        const criteriaRating = rubricAnnotation?.criteriaRatings?.find(
          (cr: any) => cr.criteria === criteria.criteria,
        );
        const weight = (criteria.weight || 0) / totalWeight;
        const score = criteriaRating?.score || 0;
        return acc + weight * score;
      }, 0);

      weightedScoreMap[candidate.id] = weightedScore > 0 ? weightedScore : 0;
    });

    return weightedScoreMap;
  }, [candidates, allResponses, rubricField.enableWeightedScore, rubricField.field_id]);

  const columnHelper = DataTable.createColumnHelper<Record<string, any>>();

  const columns = [
    columnHelper.accessor('criteria', {
      id: 'criteria',
      header: function headerComponent() {
        return (
          <div className="w-full text-center">
            <DataTable.CellContentDefault>Criteria</DataTable.CellContentDefault>
          </div>
        );
      },
      cell: function cellComponent(props: any) {
        return (
          <div className="w-full text-center">
            <Text>{props.getValue()}</Text>
          </div>
        );
      },
    }),
    ...candidates.map((candidate, index) =>
      columnHelper.accessor(`response_${index}`, {
        id: `response_${index}`,
        header: function headerComponent() {
          return (
            <div className="flex items-center justify-center w-full text-center">
              <DataTable.CellContentDefault>
                <div className="flex flex-col items-center justify-center">
                  <div className="text-center">Response {index + 1}</div>
                  {responseScoreField && (
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100">
                      <Text size="1" className="text-primary-700">
                        {getResponseScore(allResponses[candidate.id], responseScoreField.field_id)}
                      </Text>
                    </div>
                  )}
                </div>
              </DataTable.CellContentDefault>
            </div>
          );
        },
        cell: function cellComponent(props: any) {
          if (!rubricField.dimensions || rubricField.dimensions.length === 0) {
            const value = props.getValue() || 'N/A';
            const rating = rubricField.ratings?.find(rating => rating.value === value);
            return (
              <div
                className={cn(
                  'px-3 py-1 rounded-md text-center border border-neutral-200',
                  value === 'N/A' && 'bg-neutral-50',
                  rating?.color && `bg-${rating?.color}-100`,
                )}
              >
                {value}
              </div>
            );
          }

          const values = (props.getValue() || {}) as { [dimension: string]: RubricDimensionRating };
          return (
            <div className="flex flex-col gap-1 px-3 py-1">
              {rubricField.dimensions.map(dimension => {
                const rubricDimensionRating = values[dimension.value] ?? { rating: 'N/A' };
                const rating = dimension.ratings.find(
                  rating => rating.value === rubricDimensionRating.rating,
                );
                return (
                  <div
                    key={dimension.value}
                    className={cn(
                      'rounded-md text-center border border-neutral-200',
                      rubricDimensionRating.rating === 'N/A' && 'bg-neutral-50',
                      rating?.color && `bg-${rating.color}-100`,
                    )}
                  >
                    {`${dimension.value}: ${rubricDimensionRating.rating}`}
                  </div>
                );
              })}
            </div>
          );
        },
      }),
    ),
  ];

  const rows = candidates
    .flatMap(candidate => {
      const response = allResponses[candidate.id];
      const rubricAnnotation = getRubricAnnotation(response, rubricField.field_id);
      const criterias = rubricAnnotation?.criterias || [];

      return criterias.map((criteria: any) => {
        const row: any = {
          criteria: criteria.criteria,
        };

        candidates.forEach((c, index) => {
          const rubricAnnotation = getRubricAnnotation(allResponses[c.id], rubricField.field_id);
          const criteriaRating = rubricAnnotation?.criteriaRatings?.find(
            (cr: any) => cr.criteria === criteria.criteria,
          );

          if (!rubricField.dimensions || rubricField.dimensions.length === 0) {
            row[`response_${index}`] = criteriaRating?.rating || '';
          } else {
            row[`response_${index}`] = criteriaRating?.ratings || {};
          }
        });

        return row;
      });
    })
    .filter((row, index, self) => index === self.findIndex(r => r.criteria === row.criteria));

  const responseRankingCard = (
    scoredResponses: Record<number, CandidateMessageWithId[]>,
    title: string,
  ) => (
    <Card className="mx-4">
      <div className="flex flex-col gap-2">
        <Text size="2" weight="bold">
          {title}
        </Text>
        <div className="flex gap-8 justify-center">
          {Object.entries(scoredResponses).map(([score, candidatesWithScore]) => (
            <div key={score} className="flex flex-col items-center gap-2 w-25">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100">
                <Text className="text-primary-700 font-medium">{score}</Text>
              </div>
              <div className="flex flex-col items-center gap-1">
                {candidatesWithScore.map(c => (
                  <Text key={c.id} size="2" weight="bold" className="text-neutral-700">
                    Response {candidates.findIndex(candidate => candidate.id === c.id) + 1}
                  </Text>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );

  return rows.length > 0 ? (
    <div className="flex flex-col gap-4 m-4">
      {!_.isEmpty(scoredResponses) &&
        responseRankingCard(scoredResponses, 'Overall Response Scores')}

      {rubricField.enableWeightedScore && !_.isEmpty(weightedScoreMap) && (
        <Card className="mx-4">
          <div className="flex flex-col gap-2">
            <Text size="2" weight="bold">
              Response Scores (Weighted)
            </Text>
            <div className="flex flex-col gap-4">
              {Object.entries(weightedScoreMap).map(([candidateId, score]) => {
                const responseIndex = candidates.findIndex(c => c.id === candidateId) + 1;
                return (
                  <div key={candidateId} className="flex items-center gap-2">
                    <Text size="2" className="w-24">
                      Response {responseIndex}
                    </Text>
                    <Progress value={score * 100} variant="surface" />
                    <Text size="2" className="w-16 text-right font-bold">
                      {`${(score * 100).toFixed(0)}%`}
                    </Text>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {rubricField.enableWeightedScore &&
        !_.isEmpty(weightedScoreMap) &&
        responseRankingCard(
          Object.fromEntries(
            Object.entries(weightedScoreMap)
              .sort(([, scoreA], [, scoreB]) => scoreB - scoreA)
              .reduce((acc, [candidateId, score], index, arr) => {
                const prevEntry = arr[index - 1];
                const prevScore = prevEntry ? weightedScoreMap[prevEntry[0]] : null;
                const rank = score === prevScore ? acc[acc.length - 1][0] : acc.length + 1;

                if (score === prevScore) {
                  acc[acc.length - 1][1].push(candidates.find(c => c.id === candidateId)!);
                } else {
                  acc.push([rank, [candidates.find(c => c.id === candidateId)!]]);
                }
                return acc;
              }, [] as [number, CandidateMessageWithId[]][]),
          ),
          'Response Ranking with Weighted Scores',
        )}
      <DataTable.Root
        className="mx-4"
        columns={columns}
        data={rows}
        enableRowSelection={false}
        enableSorting={false}
        enableFilters={false}
        manualPagination
      >
        {({ table }) => (
          <DataTable.Table>
            <DataTable.Header>
              {table.getHeaderGroups().map(headerGroup => (
                <DataTable.HeaderGroup key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <DataTable.ColumnHeaderCell key={header.id} header={header} />
                  ))}
                </DataTable.HeaderGroup>
              ))}
            </DataTable.Header>
            <DataTable.Body>
              {table.getRowModel().rows.map(row => (
                <DataTable.Row key={row.id} row={row}>
                  {row.getVisibleCells().map(cell => (
                    <DataTable.Cell key={cell.id} cell={cell} />
                  ))}
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable.Table>
        )}
      </DataTable.Root>
    </div>
  ) : null;
};

export default RubricSummaryTable;
